import { defineStore } from 'pinia'

/**
 * AuthData Store
 * Manages authenticated user data for all user qualities (individuel, collectif, firme)
 * - individuel: Uses userData (user-data entity)
 * - collectif: Uses userData + barData (bar entity)
 * - firme: Uses userData + firmData (firm entity)
 */
export const useAuthDataStore = defineStore('authDataStore', {
  persist: {
    pick: ['userData', 'barData', 'firmData'],
    storage: piniaPluginPersistedstate.sessionStorage()
  },
  state: () => ({
    userData: null,
    barData: null,
    firmData: null
  }),
  actions: {
    setData(userData = null, barData = null, firmData = null) {
      this.userData = userData
      this.barData = barData
      this.firmData = firmData
    },
    clearData() {
      this.userData = null
      this.barData = null
      this.firmData = null
    }
  },
  getters: {
    // User quality detection
    userQuality: (state) => {
      if (state.firmData) return 'firme'
      if (state.barData) return 'collectif'
      return 'individuel'
    },
    isIndividuel: (state) => {
      return !state.barData && !state.firmData
    },
    isCollectif: (state) => {
      return !!state.barData
    },
    isFirme: (state) => {
      return !!state.firmData
    },

    // Entity data (returns the active entity based on quality)
    entityData: (state) => {
      if (state.firmData) return state.firmData
      if (state.barData) return state.barData
      return state.userData
    },

    // Entity-aware getters (work across all qualities)
    entityStatus: (state) => {
      if (state.firmData) return state.firmData.firm_status
      if (state.barData) return state.barData.bar_status
      return state.userData?.user_status
    },
    entityType: (state) => {
      if (state.firmData) return state.firmData.firm_type
      if (state.barData) return state.barData.bar_type
      return state.userData?.user_type
    },
    entityName: (state) => {
      if (state.firmData) return state.firmData.name
      if (state.barData) return state.barData.name
      if (!state.userData) return null
      const { title, firstname, lastname } = state.userData
      return [title, firstname, lastname].filter(Boolean).join(' ')
    },
    documentId: (state) => state.userData?.documentId || state.barData?.documentId || state.firmData?.documentId,
    sugarId: (state) => state.userData?.sugar_id || state.barData?.sugar_id || state.firmData?.sugar_id,
    userType: (state) => state.userData?.user_type || state.barData?.bar_type || state.firmData?.firm_type,
    userStatus: (state) => state.userData?.user_status || state.barData?.bar_status || state.firmData?.firm_status,
    fullname: (state) => {
      if (!state.userData) return null
      const { title, firstname, lastname } = state.userData
      return [title, firstname, lastname].filter(Boolean).join(' ')
    },
    membershipContribution: (state) => state.userData?.membership_contribution ?? null
  }
})
